/** Presentation helpers for money, numbers, dates, and text. */

export function toNumber(value: number | string | null | undefined): number {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

/** Currency formatting (India-first; the API exposes no currency code). */
export function formatCurrency(value: number | string | null | undefined, currencyCode = 'INR'): string {
  const amount = toNumber(value);
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currencyCode,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `₹${amount.toFixed(2)}`;
  }
}

/** Compact Indian currency for summary tiles: ₹3.42L, ₹1.20Cr, ₹89,420. */
export function formatCompactCurrency(value: number | string | null | undefined): string {
  const amount = toNumber(value);
  if (Math.abs(amount) >= 1e7) {
    return `₹${(amount / 1e7).toFixed(2)}Cr`;
  }
  if (Math.abs(amount) >= 1e5) {
    return `₹${(amount / 1e5).toFixed(2)}L`;
  }
  return `₹${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(amount)}`;
}

/** Compact whole-number formatting (e.g. 1,248). */
export function formatNumber(value: number | string | null | undefined): string {
  return new Intl.NumberFormat('en-IN').format(toNumber(value));
}

/** Percentage with sign, e.g. "+12.2%". */
export function formatPercentDelta(value: number | null | undefined): string {
  const amount = toNumber(value);
  const sign = amount > 0 ? '+' : '';
  return `${sign}${amount.toFixed(1)}%`;
}

/**
 * Normalise a date value to a millisecond timestamp. Accepts an ISO string or a
 * Java-style date array `[year, month(1-based), day, hour, minute, second, ns]`
 * (as returned by some backend endpoints), returning NaN when unparseable.
 */
function toTimestamp(value: string | number[] | null | undefined): number {
  if (!value) {
    return Number.NaN;
  }
  if (Array.isArray(value)) {
    const [year, month, day, hour = 0, minute = 0, second = 0] = value;
    if (year == null || month == null || day == null) {
      return Number.NaN;
    }
    return new Date(year, month - 1, day, hour, minute, second).getTime();
  }
  return Date.parse(value);
}

export function formatDate(value: string | number[] | null | undefined): string {
  const parsed = toTimestamp(value);
  if (!Number.isFinite(parsed)) {
    return '';
  }
  return new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }).format(parsed);
}

export function formatDateTime(value: string | number[] | null | undefined): string {
  const parsed = toTimestamp(value);
  if (!Number.isFinite(parsed)) {
    return '';
  }
  return new Intl.DateTimeFormat('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(parsed);
}

/**
 * Unambiguous, comma-free local timestamp for machine-readable output such as
 * CSV exports: "YYYY-MM-DD HH:mm:ss". Spreadsheet apps parse this correctly,
 * unlike locale-formatted strings (e.g. "29 Jun 2026, 9:00 pm") which they may
 * silently misinterpret as the wrong date.
 */
export function formatTimestamp(value: string | number[] | Date | null | undefined = new Date()): string {
  const timestamp = value instanceof Date ? value.getTime() : toTimestamp(value);
  if (!Number.isFinite(timestamp)) {
    return '';
  }
  const date = new Date(timestamp);
  const pad = (part: number) => String(part).padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ` +
    `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
  );
}

/** Compact relative time, e.g. "just now", "5m ago", "3h ago", "2d ago". */
export function formatRelativeTime(value: string | number[] | null | undefined): string {
  const timestamp = toTimestamp(value);
  if (!Number.isFinite(timestamp)) {
    return '';
  }
  const diffSeconds = Math.round((Date.now() - timestamp) / 1000);
  if (diffSeconds < 45) {
    return 'just now';
  }
  const diffMinutes = Math.round(diffSeconds / 60);
  if (diffMinutes < 60) {
    return `${diffMinutes}m ago`;
  }
  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24) {
    return `${diffHours}h ago`;
  }
  const diffDays = Math.round(diffHours / 24);
  if (diffDays < 30) {
    return `${diffDays}d ago`;
  }
  return formatDate(value);
}

/** Convert an UPPER_SNAKE enum to "Title case". */
export function humanizeEnum(value: string | null | undefined): string {
  if (!value) {
    return '';
  }
  return value
    .toLowerCase()
    .split(/[_\s]+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
